import { useForm, Controller } from "react-hook-form";
import Container from "../../componentes/Container";
import BarraDeNavegacao from "../../componentes/BarraDeNavegacao";
import {
  BotaoComTextoEIcone,
  GerenciadorDeMensagem,
} from "@digix-ui/componentes";
import { CampoDeTexto } from "../../componentes";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { buscarCidadesPorUf } from "../EditarLocalAtendimento/services/buscarCidadesPorUF";
import { buscarEstados } from "../EditarLocalAtendimento/services/buscarEstrados";
import { criarLocal } from "./services/criarLocal";

interface LocalAtendimentoFormData {
  nome: string;
  estado: string;
  cidade: string;
  tipoLocal: string;
  periodoIndeterminado: boolean;
  dataDeVigenciaInicial?: string;
  dataDeVigenciaFinal?: string;
}

const CadastrarLocalAtendimento = () => {
  const history = useHistory();

  const { control, handleSubmit, watch } = useForm<LocalAtendimentoFormData>({
    defaultValues: {
      nome: "",
      estado: "",
      cidade: "",
      tipoLocal: "",
      periodoIndeterminado: false,
      dataDeVigenciaInicial: "",
      dataDeVigenciaFinal: "",
    },
  });

  const { data: estados, isLoading: carregandoEstados } = useQuery({
    queryKey: ["estados"],
    queryFn: buscarEstados,
  });

  const estadoSelecionado = watch("estado");

  const { data: cidades, isLoading: carregandoCidades } = useQuery({
    queryKey: ["cidadesPorUf", estadoSelecionado],
    queryFn: () => buscarCidadesPorUf(estadoSelecionado),
    enabled: !!estadoSelecionado,
  });

  const mutation = useMutation({
    mutationFn: criarLocal,
    onSuccess: () => {
      GerenciadorDeMensagem.criarMensagem({
        tipo: "sucesso",
        icone: "far fa-check-circle",
        titulo: "Tudo certo!",
        texto: "Local cadastrado com sucesso",
      });
      history.push("/gerenciar-locais-permissoes");
    }
  });

  function onSubmit(data: LocalAtendimentoFormData) {
    const cidadeSelecionada = cidades?.find((c) => c.nome === data.cidade);

    if (!cidadeSelecionada) {
      GerenciadorDeMensagem.criarMensagem({
        tipo: "erro",
        icone: "far fa-times-circle",
        titulo: "Erro",
        texto: "Selecione uma cidade válida",
      });
      return;
    }

    const payload = {
      Descricao: data.nome,
      IdCidade: cidadeSelecionada.id,
      EhCidadeParceira: data.tipoLocal === "Parceiro",
      DataDeVigenciaInicial: data.periodoIndeterminado
        ? null
        : data.dataDeVigenciaInicial || null,
      DataDeVigenciaFinal: data.periodoIndeterminado
        ? null
        : data.dataDeVigenciaFinal || null,
    };

    mutation.mutate(payload);
  }

  return (
    <>
      <BarraDeNavegacao titulo="Cadastrar Local" comBotaoADireita={false} />
      <Container comFundo tamanhoDoContainer="grande">
        <form className="formulario" onSubmit={handleSubmit(onSubmit)}>
          <h2 className="cartao__titulo">Informações do local</h2>

          <div className="u-margem-superior-media formulario__ajuste-alinhamento">
            <Controller
              name="nome"
              control={control}
              rules={{ required: "A descrição do local é obrigatória" }}
              render={({ field, fieldState }) => (
                <CampoDeTexto
                  {...field}
                  label="Nome do local"
                  placeholder="Digite o nome"
                  erro={fieldState.error?.message}
                  onBlur={() => {}}
                />
              )}
            />
          </div>

          <div className="grade__linha u-margem-superior-pequena">
            <Controller
              name="estado"
              control={control}
              render={({ field }) => (
                <div className="grade__coluna_6">
                  <label className="formulario__label">Estado</label>
                  <select
                    className="formulario__input"
                    {...field}
                    disabled={carregandoEstados}
                  >
                    <option value="">Selecione</option>
                    {estados?.map((estado) => (
                      <option key={estado.id} value={estado.uf}>
                        {estado.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
            <Controller
              name="cidade"
              control={control}
              render={({ field }) => (
                <div className="grade__coluna_6">
                  <label className="formulario__label">Cidade</label>
                  <select
                    className="formulario__input"
                    {...field}
                    disabled={carregandoCidades || !estadoSelecionado}
                  >
                    <option value="">Selecione</option>
                    {cidades?.map((cidade) => (
                      <option key={cidade.id} value={cidade.nome}>
                        {cidade.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>

          <fieldset className="u-margem-superior-media">
            <legend className="formulario__label">Tipo de local</legend>
            <div className="formulario__radios">
              <Controller
                name="tipoLocal"
                control={control}
                render={({ field }) => (
                  <>
                    <label>
                      <input
                        type="radio"
                        value="AGEHAB"
                        checked={field.value === "AGEHAB"}
                        onChange={() => field.onChange("AGEHAB")}
                      />
                      AGEHAB
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="Parceiro"
                        checked={field.value === "Parceiro"}
                        onChange={() => field.onChange("Parceiro")}
                      />
                      Parceiro
                    </label>
                  </>
                )}
              />
            </div>
          </fieldset>

          <fieldset className="u-margem-superior-grande">
            <legend className="cartao__titulo">Período de vigência</legend>
            <div className="u-margem-superior-pequena">
              <label>O período é indeterminado?</label>
              <div className="formulario__radios u-margem-top-pequena">
                <Controller
                  name="periodoIndeterminado"
                  control={control}
                  render={({ field }) => (
                    <>
                      <label>
                        <input
                          type="radio"
                          checked={!field.value}
                          onChange={() => field.onChange(false)}
                        />
                        Não
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={field.value}
                          onChange={() => field.onChange(true)}
                        />
                        Sim
                      </label>
                    </>
                  )}
                />
              </div>
            </div>

            {!watch("periodoIndeterminado") && (
              <div className="grade__linha u-margem-superior-pequena u-alinha-itens-ao-fim">
                <Controller
                  name="dataDeVigenciaInicial"
                  control={control}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      value={field.value ?? ""}
                      label="Data de início"
                      tipo="date"
                      colunas="5"
                      onBlur={() => {}}
                    />
                  )}
                />
                <div className="grade__coluna grade__coluna_2 u-texto-alinhado-ao-centro u-margem-top-grande">
                  até
                </div>
                <Controller
                  name="dataDeVigenciaFinal"
                  control={control}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      value={field.value ?? ""}
                      label="Data de fim"
                      tipo="date"
                      colunas="5"
                      onBlur={() => {}}
                    />
                  )}
                />
              </div>
            )}
          </fieldset>

          <div className="formulario__botoes">
            <BotaoComTextoEIcone
              tamanho="pequeno"
              cor="secundaria"
              tipo="button"
              icone="far fa-arrow-left"
              posicaoDoIcone="esquerda"
              onClick={() => history.goBack()}
            >
              Voltar
            </BotaoComTextoEIcone>
            <BotaoComTextoEIcone
              tamanho="pequeno"
              cor="sucesso"
              tipo="submit"
              icone="far fa-check"
              posicaoDoIcone="esquerda"
            >
              Cadastrar
            </BotaoComTextoEIcone>
          </div>
        </form>
      </Container>
    </>
  );
};

export default CadastrarLocalAtendimento;
